import { ColDef } from "@ag-grid-community/core";
import { SDStrategy, SPStrategy } from "@front/m19-api-client";
import {
  BulkUploadPreviewStatus,
  BulkUploadRunStatus,
} from "@m19-board/bulk-upload/bulk-upload-grid-cells/bulk-upload-type-icon.component";
import { BehaviorSubject, Observable, of, ReplaySubject, switchMap, take, tap } from "rxjs";
import { inject, Injectable } from "@angular/core";
import { BULK_UPLOAD_HANDLER } from "./bulk-upload.tokens";
import { PromoDaysBoost } from "./promo-boost/promo-boost-bulk-upload-handler.service";
import { TacosBulkUploadEntity } from "./tacos/tacos-bulk-upload-handler.service";

export interface BulkUploadHandler<T> {
  readonly progress$: Observable<BulkUploadProgressCounter>;
  readonly expectedHeaders: string[];
  getPreviewGridColumnsDefs(): ColDef<BulkUploadRowPreview<T>>[];
  getRunResultGridColumnsDefs(): ColDef<BulkUploadRunResultItem<T>>[];
  buildPreview(rows: string[]): Observable<BulkUploadBuckets<T>>;
  run(buckets: BulkUploadBuckets<T>): Observable<BulkUploadRunResultItem<T>[]>;
  export(): void;
}

export function parseBulkUploadRows(content: string, expectedHeaders: string[]): string[] {
  const rows = content.split(/\n+/).filter((row) => row.trim() !== "");
  if (rows.length === 0) return [];
  return looksLikeHeader(rows[0], expectedHeaders) ? rows.slice(1) : rows;
}

function looksLikeHeader(firstRow: string, expectedHeaders: string[]): boolean {
  const tokens = firstRow.split(/\t/).map((t) => t.trim().toLowerCase());
  if (tokens.length === 0) return false;
  const expected = new Set(expectedHeaders.map((h) => h.toLowerCase()));
  return tokens.every((token) => expected.has(token));
}

export interface BulkUploadProgressCounter {
  completed: number;
  total: number;
}

export interface BulkUploadRunResultItem<T> {
  parsedEntity: Partial<T>;
  resultEntity: T | null;
  error?: string;
  status: BulkUploadRunStatus;
}

export type BulkUploadBuckets<T> = BulkUploadRowPreview<T>[];

export interface BulkUploadRowPreview<T> {
  parsedEntity: Partial<T>;
  fieldsPreview: BulkUploadFieldPreview<T>[];
  status: BulkUploadPreviewStatus;
}

export interface BulkUploadFieldPreview<T> {
  field: keyof T;
  providedValue: string;
  parsedValue: T[keyof T];
  error?: string;
  existingValue?: T[keyof T];
  hasChanged?: boolean;
  isCreation?: boolean;
}

export interface FileMetadata {
  name: string;
  size: number;
  type: string;
  rows: string[];
}

export interface BulkFlowState {
  step: "upload" | "preview" | "run";
  loading: boolean;
}

export type SupportedBulkUploadEntityModel = SPStrategy | SDStrategy | PromoDaysBoost | TacosBulkUploadEntity;

@Injectable()
export class BulkUploadContextService {
  private readonly handler = inject(BULK_UPLOAD_HANDLER);

  private readonly fileMetadata: ReplaySubject<FileMetadata | null> = new ReplaySubject(1);
  public readonly fileMetadata$ = this.fileMetadata.asObservable();

  private readonly bulkFlowState: BehaviorSubject<BulkFlowState> = new BehaviorSubject<BulkFlowState>({
    step: "upload",
    loading: false,
  });
  public readonly bulkFlowState$ = this.bulkFlowState.asObservable();

  private readonly previewBuckets: ReplaySubject<BulkUploadBuckets<SupportedBulkUploadEntityModel> | null> =
    new ReplaySubject(1);
  public readonly previewBuckets$ = this.previewBuckets.asObservable();

  private readonly runResult: ReplaySubject<BulkUploadRunResultItem<SupportedBulkUploadEntityModel>[] | null> =
    new ReplaySubject(1);
  public readonly runResult$ = this.runResult.asObservable();

  public readonly progress$: Observable<BulkUploadProgressCounter> = this.handler.progress$;

  upload(file: File): void {
    this.readFile(file);
  }

  uploadPlainText(content: string): void {
    this.fileMetadata.next({
      name: "",
      size: content.length,
      type: "text/plain",
      rows: parseBulkUploadRows(content, this.handler.expectedHeaders),
    });
  }

  preview() {
    this.patchState({
      step: "preview",
      loading: true,
    });

    return this.fileMetadata$.pipe(
      take(1),
      switchMap((metadata) => (metadata ? this.handler.buildPreview(metadata!.rows) : of(null))),
      tap((buckets) => {
        this.previewBuckets.next(buckets);
        this.patchState({
          step: "preview",
          loading: false,
        });
      }),
    );
  }

  run() {
    this.patchState({
      step: "run",
      loading: true,
    });

    return this.previewBuckets$.pipe(
      take(1),
      switchMap((buckets) => (buckets ? this.handler.run(buckets) : of(null))),
      tap((result) => {
        this.runResult.next(result);
        this.patchState({
          step: "run",
          loading: false,
        });
      }),
    );
  }

  export() {
    this.handler.export();
  }

  public getPreviewGridColumnsDefs(): ColDef<BulkUploadRowPreview<SupportedBulkUploadEntityModel>>[] {
    return this.handler.getPreviewGridColumnsDefs();
  }

  public getRunResultGridColumnsDefs(): ColDef<BulkUploadRunResultItem<SupportedBulkUploadEntityModel>>[] {
    return this.handler.getRunResultGridColumnsDefs();
  }

  backToUpload() {
    this.patchState({
      step: "upload",
      loading: false,
    });
  }

  removeFile() {
    this.fileMetadata.next(null);
  }

  resetImport() {
    this.fileMetadata.next(null);
    this.previewBuckets.next(null);
    this.runResult.next(null);
    this.patchState({
      step: "upload",
      loading: false,
    });
  }

  private readFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (event) => {
      const fileAsText = event.target?.result as string;
      this.fileMetadata.next({
        name: file.name,
        size: file.size,
        type: file.type,
        rows: parseBulkUploadRows(fileAsText, this.handler.expectedHeaders),
      });
    };
    reader.readAsText(file);
  }

  private patchState(state: BulkFlowState): void {
    this.bulkFlowState.next(state);
  }
}

export function rowHasFieldError<T>(fieldsPreview: BulkUploadFieldPreview<T>[]): boolean {
  return fieldsPreview.some((field) => !!field.error);
}

export function rowIsUnchanged<T>(fieldsPreview: BulkUploadFieldPreview<T>[]): boolean {
  return fieldsPreview.every((field) => !field.hasChanged && !field.error && !field.isCreation);
}
